import Cookies from "js-cookie";
import {
  AttributionSearch,
  SeoDataType,
  validateAttributionSearch,
} from "router/routes/betterAuthSearch";

import { STOTLES_UTM_COOKIE, VisitInfo } from "lib/UtmParams";

/**
 * Mapping of UTM query parameter keys to their corresponding un-prefixed keys used in legacy tracking.
 */
export type SignupContextPayload = {
  signup_utm: {
    referrer: string;
    url: string;
    utmParams: Record<string, string>;
    last_page_url_before_form_submit?: string;
  };
  seo_params?: { id?: string; dataType?: SeoDataType };
};

/**
 * Mapping of UTM query parameter keys to their corresponding un-prefixed keys used in legacy tracking.
 * The un-prefixed keys (right-hand side) must stay in sync with the utmParams shape in
 * nodejs/lib/data/db/schemas/public/user.schema.ts (crmData.signup_utm.utmParams).
 */
const UTM_PARAM_KEYS: Array<[keyof AttributionSearch, string]> = [
  ["utm_source", "source"],
  ["utm_medium", "medium"],
  ["utm_campaign", "campaign"],
  ["utm_term", "term"],
  ["utm_content", "content"],
  ["utm_id", "id"],
  ["utm_adgroup", "adgroup"],
  ["utm_referrer", "referrer"],
  ["gclid", "gclid"],
  ["gbraid", "gbraid"],
  ["wbraid", "wbraid"],
  ["utm_source_stotles", "source_stotles"],
];

/**
 * The `stotles_utm` first-touch cookie, recast as the URL params it stands in for and run
 * through the same validation. Empty when absent or unparseable: the webapp, SEO app and Webflow all write it.
 */
function cookieAttribution(): AttributionSearch {
  const raw = Cookies.get(STOTLES_UTM_COOKIE);
  if (!raw) return {};

  let visit: Partial<VisitInfo>;
  try {
    visit = JSON.parse(raw);
  } catch {
    return {};
  }
  if (!visit || typeof visit !== "object") return {};

  const search: Record<string, unknown> = {};
  for (const [searchKey, utmKey] of UTM_PARAM_KEYS) {
    search[searchKey] = visit.utmParams?.[utmKey];
  }
  // A landing URL's own utm_referrer, else the referrer the writer saw.
  search.utm_referrer = visit.utmParams?.referrer || visit.referrer;
  search.utm_fp = visit.url;
  search.utm_lp = visit.last_page_url_before_form_submit;
  return validateAttributionSearch(search);
}

/**
 * Build the signup attribution context from the sanitised URL search params + the current page,
 * mirroring the legacy `captureVisitInfo` (referrer + landing URL + utm params). Sent to the
 * auth-service on signUp so it lands in users.crm_data.signup_utm.
 *
 * The link wins: it needs no cookie consent and describes the visit that converted. The cookie
 * fills in when the link arrives bare, e.g. from a second Webflow page or a direct visit.
 */
export function buildSignupContext(search: AttributionSearch): SignupContextPayload {
  const cookie = cookieAttribution();
  // Taken whole from one source, so a signup never mixes one visit's source with another's campaign.
  // A referrer alone is not a campaign: Webflow's decorator can forward one with nothing else.
  const linkHasCampaign = UTM_PARAM_KEYS.some(
    ([searchKey]) => searchKey !== "utm_referrer" && search[searchKey],
  );
  const campaign = linkHasCampaign ? search : cookie;

  const utmParams: Record<string, string> = {};
  for (const [searchKey, utmKey] of UTM_PARAM_KEYS) {
    if (searchKey === "utm_referrer") continue;
    const value = campaign[searchKey];
    if (typeof value === "string" && value) utmParams[utmKey] = value;
  }
  // Resolved per field like the pages below, so the record holds one referrer, not two.
  const forwardedReferrer = search.utm_referrer || cookie.utm_referrer;
  if (forwardedReferrer) utmParams.referrer = forwardedReferrer;

  const lastPage = search.utm_lp || cookie.utm_lp;
  const context: SignupContextPayload = {
    signup_utm: {
      // Without a forwarded referrer and first page these record /auth/register and its
      // referrer, which is always our own page, so both are useless for attribution.
      referrer: forwardedReferrer || document.referrer,
      url: search.utm_fp || cookie.utm_fp || window.location.href,
      utmParams,
      ...(lastPage ? { last_page_url_before_form_submit: lastPage } : {}),
    },
  };

  if (search.seo_id || search.seo_data_type) {
    context.seo_params = { id: search.seo_id, dataType: search.seo_data_type };
  }

  return context;
}
